import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Banknote, TrendingUp, MinusCircle, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;

const KpiCard = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <div className="bg-white border border-border p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent ? "text-teal-600" : "text-slate-400"}`} strokeWidth={2} />
    </div>
    <p className="text-2xl font-bold text-[var(--brand-primary)] mt-3 tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <Card className="rounded-none border-border shadow-sm" data-testid={testId}>
    <CardHeader className="pb-2 border-b border-border">
      <CardTitle className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-5">{children}</CardContent>
  </Card>
);

export const PayrollDashboard = () => {
  const [data, setData] = useState(null);
  const [memoBusy, setMemoBusy] = useState(false);

  useEffect(() => {
    api.get("/payroll/dashboard").then((r) => setData(r.data)).catch(() => setData({ trend: [] }));
  }, []);

  if (!data) return <Skeleton className="h-96 w-full rounded-none" />;

  const varianceMemo = async () => {
    setMemoBusy(true);
    try {
      await downloadFile("/payroll/variance-memo/pdf", "payroll-variance-memo.pdf");
      toast.success("Variance memo generated — narrative drafted via the masked gateway");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Memo generation failed"); }
    finally { setMemoBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="payroll-dashboard">
      <div className="flex justify-end">
        <Button data-testid="variance-memo-btn" variant="outline" onClick={varianceMemo} disabled={memoBusy}
          className="rounded-none h-9 text-[12px] border-teal-600/50 text-teal-700 hover:bg-teal-50">
          <FileText className="h-4 w-4" /> {memoBusy ? "Drafting memo…" : "Variance Memo (PDF)"}
        </Button>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={Banknote} label={`Net Payroll — ${data.latest_period || "—"}`}
          value={fmtQar(data.latest_totals?.net)} sub={`status: ${data.latest_status || "—"}`}
          testId="kpi-net-payroll" accent />
        <KpiCard icon={TrendingUp} label="Incentives" value={fmtQar(data.latest_totals?.incentives)}
          sub="KPI tiers + certifications + dept multiplier" testId="kpi-incentives" accent />
        <KpiCard icon={MinusCircle} label="Deductions" value={fmtQar(data.latest_totals?.deductions)}
          sub="pension — Qatari nationals" testId="kpi-deductions" />
        <KpiCard icon={AlertTriangle} label="Exceptions" value={data.latest_exceptions}
          sub="net change > 20% or missing KPI" testId="kpi-exceptions" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard title="Payroll Cost Trend (net, QAR)" testId="chart-payroll-trend">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.trend} margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtQar(v)} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="net" fill="var(--brand-primary)" maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost by Department — latest run (QAR)" testId="chart-cost-by-dept">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.cost_by_department} margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="department" tick={{ fontSize: 11, fill: "#64748B" }} interval={0} angle={-14} height={44} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtQar(v)} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="net" fill="var(--brand-accent)" maxBarSize={36} />
              <Bar dataKey="incentives" fill="#94A3B8" maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="bg-white border border-border" data-testid="trend-table">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Run history</p>
        </div>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              {["Period", "Status", "Headcount", "Gross", "Incentives", "Deductions", "Net", "Exceptions"].map((h) => (
                <th key={h} className="px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.trend.slice().reverse().map((t) => (
              <tr key={t.period} data-testid={`trend-row-${t.period}`}>
                <td className="px-4 py-2 font-mono">{t.period}</td>
                <td className="px-4 py-2 uppercase text-[10px] font-mono text-slate-600">{t.status}</td>
                <td className="px-4 py-2">{t.headcount}</td>
                <td className="px-4 py-2 font-mono">{fmtQar(t.gross)}</td>
                <td className="px-4 py-2 font-mono text-teal-700">{fmtQar(t.incentives)}</td>
                <td className="px-4 py-2 font-mono">{fmtQar(t.deductions)}</td>
                <td className="px-4 py-2 font-mono font-semibold">{fmtQar(t.net)}</td>
                <td className="px-4 py-2">{t.exceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollDashboard;
